import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Upload, Download, FileText, CheckCircle, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { companyService } from '@/services/companyService';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { toStrictSku, createSkuSet } from '@/lib/skuUtils';

const PRODUCT_CATEGORY: CategoryKey = 'product';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface ImportError { row: number; field: string; message: string; }
interface ImportPreviewData { [key: string]: any; }

const Product3PLImport: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ImportPreviewData[]>([]);
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [warnings, setWarnings] = useState<ImportError[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importStatus, setImportStatus] = useState<'idle' | 'preview' | 'success' | 'error'>('idle');
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [currentCompany, setCurrentCompany] = useState<{ id: string; code?: string; name?: string } | null>(null);
  const [companySkuSet, setCompanySkuSet] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    (async () => {
      try {
        const company = await companyService.getCurrentCompany();
        if (company) {
          setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
          // Prefetch all SKU IDs for quick validation
          const { supabase } = await import('@/integrations/supabase/client');
          const companyIdNum = Number(company.id);
          try {
            const { data, error } = await supabase
              .from('products')
              .select('sku_id')
              .eq('company_id', companyIdNum);
            if (!error && Array.isArray(data)) {
              // 使用嚴格的 SKU 處理邏輯，保持精確性
              const skuList = data.map(r => r?.sku_id).filter(Boolean);
              setCompanySkuSet(createSkuSet(skuList));
              return;
            }
          } catch {}
          setCompanySkuSet(new Set<string>());
        }
      } catch {}
    })();
  }, []);

  // Supported/target columns
  const requiredColumns = ['3pl_stock_qty', '3pl_can_order_qty', '3pl_outgoing_qty'];

  const toNumberOrNull = (v: any) => {
    const t = typeof v === 'string' ? v.trim() : v;
    if (t === '' || t === null || t === undefined) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  // Security: Sanitize text to prevent XSS and Excel Formula Injection
  const sanitizeText = (text: string): string => {
    if (!text || typeof text !== 'string') return text;

    let sanitized = text.trim();

    // HTML escape to prevent XSS while preserving content (e.g., "<此日期前最佳：2026.12>" becomes "&lt;此日期前最佳：2026.12&gt;")
    // Escape order matters: & must be escaped first to avoid double-escaping
    sanitized = sanitized
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    // Prevent Excel Formula Injection by prefixing dangerous formulas with '
    if (/^[=+\-@]/.test(sanitized)) {
      sanitized = "'" + sanitized;
    }

    // Additional security: Limit text length to prevent DoS
    if (sanitized.length > 5000) {
      sanitized = sanitized.substring(0, 5000) + '...';
    }

    return sanitized;
  };

  // Security: Validate numeric fields with range checks
  const validateNumericField = (value: any, fieldName: string, min: number = 0): { isValid: boolean; error?: string } => {
    const num = toNumberOrNull(value);
    if (num === null) return { isValid: true }; // Allow null values

    if (!Number.isFinite(num)) {
      return { isValid: false, error: language === 'zh' ? `${fieldName}必須是有效的數字` : `${fieldName} must be a valid number` };
    }

    if (num < min) {
      return { isValid: false, error: language === 'zh' ? `${fieldName}不能小於 ${min}` : `${fieldName} cannot be less than ${min}` };
    }

    // Additional check for extremely large numbers (potential DoS)
    if (num > 999999999) {
      return { isValid: false, error: language === 'zh' ? `${fieldName}數值過大，請檢查數據` : `${fieldName} value too large, please check data` };
    }

    return { isValid: true };
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Security: File size limit to prevent DoS attacks
      const MAX_SIZE = 5 * 1024 * 1024; // 5MB limit
      if (file.size > MAX_SIZE) {
        toast.error(language === 'zh' ? '檔案太大！請上傳 5MB 以下的檔案。' : 'File too large! Please upload files under 5MB.');
        if (event.target) {
          (event.target as HTMLInputElement).value = '';
        }
        return;
      }

      if (!currentCompany?.id) {
        toast.error(language === 'zh' ? '尚未選擇公司，請先切換公司後再試' : 'No company selected. Please switch company and try again');
        (event.target as HTMLInputElement).value = '';
        return;
      }
      setSelectedFile(file);
      parseFile(file);
    }
  };

  const parseFile = (file: File) => {
    setIsProcessing(true);
    setImportStatus('preview');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsedData: ImportPreviewData[] = [];
        const validationErrors: ImportError[] = [];
        const nonBlockingWarnings: ImportError[] = [];
        const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
        let headers: string[] = [];
        let dataRows: string[][] = [];
        if (isExcel) {
          const wb = XLSX.read(new Uint8Array(e.target?.result as ArrayBuffer), { type: 'array' });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json<any>(sheet, { header: 1, defval: '' }) as any[];
          if (rows.length < 2) throw new Error('檔案至少需一行標題與一行資料');
          headers = (rows[0] as any[]).map((v) => String(v));
          dataRows = rows.slice(1) as any[];
        } else {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((r) => r.trim());
          if (lines.length < 2) throw new Error('檔案至少需一行標題與一行資料');
          headers = lines[0].split(',').map((h) => h.trim().replace(/"/g, ''));
          dataRows = lines.slice(1).map((line) => line.split(',').map((v) => v.trim().replace(/"/g, '')));
        }

        // Header mapping (support both ZH and EN variants commonly seen)
        const normalized = (s: string) => s?.toString().trim().toLowerCase();
        const headerMap: Record<string, string> = {
          // Identifier columns
          'sku': 'sku_raw',
          'sku id': 'sku_raw',
          'sku_id': 'sku_raw',
          '產品id': 'sku_raw',
          '產品 id': 'sku_raw',
          '產品ID': 'sku_raw', // 简体中文
          '條碼': 'barcode',
          '条码': 'barcode', // 简体中文
          'barcode': 'barcode',
          'bar code': 'barcode',
          'ean': 'barcode',
          'upc': 'barcode',
          '庫存數量': '3pl_stock_qty',
          '库存': '3pl_stock_qty', // 简体中文
          '現存量': '3pl_stock_qty',
          '现存量': '3pl_stock_qty', // 简体中文
          'stock qty': '3pl_stock_qty',
          'stock_qty': '3pl_stock_qty',
          '可下單數量': '3pl_can_order_qty',
          '可下单': '3pl_can_order_qty', // 简体中文
          'available qty': '3pl_can_order_qty',
          'available_qty': '3pl_can_order_qty',
          '出庫中數量': '3pl_outgoing_qty',
          '出库中': '3pl_outgoing_qty', // 简体中文
          'outgoing qty': '3pl_outgoing_qty',
          'outgoing_qty': '3pl_outgoing_qty',
        };

        const mappedKeys: (string | null)[] = headers.map((h) => headerMap[normalized(h)] || null);
        // Ensure required columns exist
        const hasAllRequired = requiredColumns.every((col) => mappedKeys.includes(col));
        const hasIdentifier = mappedKeys.includes('barcode') || mappedKeys.includes('sku_raw');
        if (!hasAllRequired) {
          throw new Error(
            language === 'zh'
              ? '找不到必要欄位（需包含：庫存數量、可下單數量、出庫中數量）。請使用 3PL 模板。'
              : 'Missing required columns (need: Stock Qty, Can Order Qty, Outgoing Qty). Please use the 3PL template.'
          );
        }
        if (!hasIdentifier) {
          throw new Error(
            language === 'zh'
              ? '找不到識別欄位：請提供「條碼」(或 EAN/UPC)；若沒有，至少提供 SKU'
              : 'Missing identifier column: provide Barcode (or EAN/UPC); otherwise provide SKU'
          );
        }

        dataRows.forEach((cols, rowIdx) => {
          if (!cols || cols.length === 0) return;
          const obj: ImportPreviewData = {};
          mappedKeys.forEach((dbKey, ci) => { if (dbKey) obj[dbKey] = cols[ci]; });
          // Security: Apply data sanitization
          const barcode = sanitizeText(toStrictSku((obj as any).barcode));
          const skuRaw = sanitizeText(toStrictSku((obj as any).sku_raw));
          obj.sku_id = barcode || skuRaw; // prefer barcode when both exist

          // Apply numeric conversions
          obj['3pl_stock_qty'] = toNumberOrNull(obj['3pl_stock_qty']);
          obj['3pl_can_order_qty'] = toNumberOrNull(obj['3pl_can_order_qty']);
          obj['3pl_outgoing_qty'] = toNumberOrNull(obj['3pl_outgoing_qty']);

          // Security: Enhanced numeric validation with range checks
          const stockValidation = validateNumericField(obj['3pl_stock_qty'], language === 'zh' ? '庫存數量' : 'Stock Qty');
          if (!stockValidation.isValid) {
            validationErrors.push({ row: rowIdx + 2, field: '3pl_stock_qty', message: stockValidation.error! });
          }

          const canOrderValidation = validateNumericField(obj['3pl_can_order_qty'], language === 'zh' ? '可下單數量' : 'Can Order Qty');
          if (!canOrderValidation.isValid) {
            validationErrors.push({ row: rowIdx + 2, field: '3pl_can_order_qty', message: canOrderValidation.error! });
          }

          const outgoingValidation = validateNumericField(obj['3pl_outgoing_qty'], language === 'zh' ? '出庫中數量' : 'Outgoing Qty');
          if (!outgoingValidation.isValid) {
            validationErrors.push({ row: rowIdx + 2, field: '3pl_outgoing_qty', message: outgoingValidation.error! });
          }

          if (!obj.sku_id) {
            validationErrors.push({ row: rowIdx + 2, field: 'sku_id', message: '缺少 SKU' });
          } else {
            // Verify SKU belongs to current company -> 警告但不阻擋匯入
            // 使用嚴格的 SKU 匹配邏輯
            const skuToCheck = toStrictSku(obj.sku_id);
            const exists = skuToCheck && companySkuSet.has(skuToCheck);
            if (!exists) {
              nonBlockingWarnings.push({ row: rowIdx + 2, field: 'sku_id', message: 'SKU 不屬於當前公司或不存在（將照常匯入參考數據）' });
            }
          }
          parsedData.push(obj);
        });

        setPreviewData(parsedData);
        setErrors(validationErrors);
        setWarnings(nonBlockingWarnings);
        if (validationErrors.length > 0) {
          toast.error(
            language === 'zh'
              ? `檔案驗證失敗，共 ${validationErrors.length} 筆錯誤。請修正後再匯入。`
              : `Validation failed with ${validationErrors.length} errors. Please fix and try again.`
          );
        } else {
          if (nonBlockingWarnings.length > 0) {
            toast.warning(
              language === 'zh'
                ? `發現 ${nonBlockingWarnings.length} 筆 SKU 未在公司產品清單內，將作為參考資料匯入`
                : `${nonBlockingWarnings.length} SKUs not found in company product list. Will import as reference`
            );
          } else {
            toast.success(language === 'zh' ? 'Excel 已準備，可隨時按下「確定滙入」' : 'Excel is ready. You can click "Confirm Import" anytime');
          }
        }
      } catch (err: any) {
        toast.error('檔案解析失敗: ' + err.message);
        setImportStatus('error');
      } finally {
        setIsProcessing(false);
      }
    };
    if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) reader.readAsArrayBuffer(file);
    else reader.readAsText(file);
  };

  const downloadTemplate = () => {
    // Create Excel workbook
    const wb = XLSX.utils.book_new();

    // Create worksheet data with SKU column as text format
    const wsData = [
      ['SKU', '庫存數量', '可下單數量', '出庫中數量'],
      ['SKU001', 100, 90, 5]
    ];

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set SKU column (A column) to text format to preserve leading zeros
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:D2');
    for (let row = 1; row <= range.e.r; row++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 }); // Column A (0-indexed)
      if (ws[cellAddress]) {
        ws[cellAddress].t = 's'; // Force text type
        ws[cellAddress].v = String(ws[cellAddress].v); // Ensure it's a string
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, '3PL Template');

    // Generate Excel file
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    // Download the file
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', '3pl_inventory_template.xlsx');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(language === 'zh' ? '模板檔案已下載' : 'Template downloaded');
  };

  const resetImport = () => {
    setSelectedFile(null);
    setPreviewData([]);
    setErrors([]);
    setImportStatus('idle');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImport = async () => {
    if (!currentCompany?.id) {
      toast.error(language === 'zh' ? '未能取得公司資訊，請稍後重試' : 'Company not ready');
      return;
    }
    if (previewData.length === 0) {
      toast.error(language === 'zh' ? '請先選擇檔案' : 'Please select a file first');
      return;
    }
    if (errors.length > 0) {
      toast.error(language === 'zh' ? '檔案仍有錯誤，請修正後再匯入' : 'There are validation errors. Please fix them before importing');
      return;
    }

    try {
      setIsProcessing(true);
      setProgress({ done: 0, total: previewData.length });
      const { supabase } = await import('@/integrations/supabase/client');

      // Security: Secure Batch Import - Process in chunks of 50 for better performance and error handling
      const BATCH_SIZE = 50;
      let success = 0;
      let failCount = 0;
      let skipped = 0; // unknown SKUs skipped
      const failedRows: Array<{ rowIndex: number; skuId: string; error: string }> = [];

      // Step 1: Prepare valid data for batch processing
      const validRows: Array<{ rowIndex: number; payload: Record<string, any> }> = [];
      for (let i = 0; i < previewData.length; i++) {
        const row = previewData[i];
        // Skip rows whose SKU is not in the current company's product list
        // 使用嚴格的 SKU 匹配邏輯
        const skuToCheck = toStrictSku(row.sku_id);
        if (!skuToCheck || !companySkuSet.has(skuToCheck)) {
          skipped += 1;
          continue;
        }

        const payload: Record<string, any> = {
          sku_id: row.sku_id,
          ref_stock_qty: row['3pl_stock_qty'] ?? null,
          ref_available_qty: row['3pl_can_order_qty'] ?? null,
          ref_ready_for_delivery: row['3pl_outgoing_qty'] ?? null,
          ref_last_update_time: new Date().toISOString(),
          // 添加 company_id
          ...(currentCompany?.id && { company_id: Number(currentCompany.id) }),
          // 生成 company_sku_key = company_id + sku_id
          ...(currentCompany?.id && { company_sku_key: `${currentCompany.id}-${row.sku_id}` })
        };

        validRows.push({ rowIndex: i, payload });
      }

      // Step 2: Process in batches of 50
      const totalBatches = Math.ceil(validRows.length / BATCH_SIZE);
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const startIdx = batchIndex * BATCH_SIZE;
        const endIdx = Math.min(startIdx + BATCH_SIZE, validRows.length);
        const batchRows = validRows.slice(startIdx, endIdx);

        try {
          // Process each row in the batch
          for (const rowData of batchRows) {
            const payload = rowData.payload;

            try {
              // 如果有重複的company_sku_key，先刪除舊的，再插入新的
              if (payload.company_sku_key) {
                // 先刪除舊記錄
                await supabase
                  .from('logistics_inventory_ref')
                  .delete()
                  .eq('company_sku_key', payload.company_sku_key);
              }

              // 插入新記錄
              const { error } = await supabase
                .from('logistics_inventory_ref')
                .insert(payload);

              if (error) {
                failCount += 1;
                failedRows.push({
                  rowIndex: rowData.rowIndex + 1,
                  skuId: payload.sku_id,
                  error: error.message || '插入失敗'
                });
                console.error(`第 ${rowData.rowIndex + 1} 行插入失敗 (SKU: ${payload.sku_id}):`, error);
              } else {
                success += 1;
              }
            } catch (err: any) {
              failCount += 1;
              failedRows.push({
                rowIndex: rowData.rowIndex + 1,
                skuId: payload.sku_id,
                error: err.message || '網路或系統錯誤'
              });
              console.error(`第 ${rowData.rowIndex + 1} 行匯入失敗 (SKU: ${payload.sku_id}):`, err);
            }
          }

          // Update progress after each batch
          const processedRows = Math.min((batchIndex + 1) * BATCH_SIZE, validRows.length);
          setProgress({ done: processedRows + (previewData.length - validRows.length), total: previewData.length });

        } catch (err: any) {
          // Batch failed - mark all remaining rows in this batch as failed
          failCount += (endIdx - startIdx);
          batchRows.forEach(rowData => {
            failedRows.push({
              rowIndex: rowData.rowIndex + 1,
              skuId: rowData.payload.sku_id,
              error: language === 'zh' ? '批次處理失敗' : 'Batch processing failed'
            });
          });
          console.warn('Batch 3PL import warning:', err?.message || err);
        }
      }
      if (failCount === 0) {
        const msgZh = `匯入完成：成功 ${success} 筆${skipped > 0 ? `（已略過 ${skipped} 筆未知 SKU）` : ''}`;
        const msgEn = `Import complete: ${success} successful${skipped > 0 ? ` (skipped ${skipped} unknown SKUs)` : ''}`;
        toast.success(language === 'zh' ? msgZh : msgEn);
        setImportStatus('success');
      } else {
        // 顯示詳細的失敗資訊
        console.error('匯入失敗的資料明細:', failedRows);

        const errorDetails = failedRows.slice(0, 3).map(row =>
          `第${row.rowIndex}行 (SKU: ${row.skuId}) - ${row.error}`
        ).join('; ');

        const msgZh = `匯入完成：成功 ${success} 筆，失敗 ${failCount} 筆。${skipped > 0 ? `（另略過 ${skipped} 筆未知 SKU）` : ''}${failedRows.length > 0 ? `\n失敗明細: ${errorDetails}${failedRows.length > 3 ? '...' : ''}` : ''}`;
        const msgEn = `Import complete: ${success} successful, ${failCount} failed. ${skipped > 0 ? `(skipped ${skipped} unknown SKUs)` : ''}${failedRows.length > 0 ? `\nError details: ${errorDetails}${failedRows.length > 3 ? '...' : ''}` : ''}`;

        toast.warning(language === 'zh' ? msgZh : msgEn);
        setImportStatus('error');
      }
    } catch (e: any) {
      console.error(e);
      toast.error(language === 'zh' ? '匯入失敗' : 'Import failed');
      setImportStatus('error');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              localStorage.setItem('selectedCategory', PRODUCT_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'zh' ? '返回主控台' : 'Back to Dashboard'}
          </Button>
          <div className="flex items-center gap-2">
            <Upload className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{language === 'zh' ? '滙入功能（3PL 物流倉庫）' : 'Import Logistics Inventory (3PL)'}
            </h1>
          </div>
        </div>

        <Card className="bg-card border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">{language === 'zh' ? '檔案上傳' : 'File Upload'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-purple-400" />
                  <div>
                    <p className="text-foreground font-medium">{language === 'zh' ? '下載匯入模板' : 'Download Template'}</p>
                    <p className="text-muted-foreground text-sm">{language === 'zh' ? '使用我們的Excel模板確保資料格式正確' : 'Use our Excel template to ensure correct format'}</p>
                  </div>
                </div>
                <Button onClick={downloadTemplate} variant="outline" className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50">
                  <Download className="w-4 h-4 mr-2" />
                  {language === 'zh' ? '下載模板' : 'Download'}
                </Button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} className="hidden" id="3pl-import-file" title={language === 'zh' ? '選擇匯入檔案（CSV 或 Excel）' : 'Choose import file (CSV or Excel)'} />
                  <Button onClick={() => fileInputRef.current?.click()} className="bg-purple-600 hover:bg-purple-700 text-white" disabled={isProcessing}>
                    <Upload className="w-4 h-4 mr-2" />
                    {language === 'zh' ? '選擇檔案' : 'Choose File'}
                  </Button>
                  {selectedFile && (
                    <div className="flex items-center gap-2">
                      <span className="text-foreground">{selectedFile.name}</span>
                      <Badge variant="outline" className="text-muted-foreground border-border">{(selectedFile.size / 1024).toFixed(1)} KB</Badge>
                      <Button variant="ghost" size="sm" onClick={resetImport} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></Button>
                    </div>
                  )}
                </div>
                {isProcessing && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
                    <span>{language === 'zh' ? '處理中...' : 'Processing...'} {progress.done} / {progress.total}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {previewData.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground">{language === 'zh' ? '資料預覽 (前 10 筆)' : 'Preview (first 10 rows)'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '條碼' : 'Barcode (SKU ID)'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '庫存數量' : 'Stock Qty'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '可下單數量' : 'Can Order Qty'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '出庫中數量' : 'Outgoing Qty'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.slice(0, 10).map((row, index) => (
                      <TableRow key={index} className="border-border hover:bg-accent/50">
                        <TableCell className="text-foreground text-sm">{String(row.sku_id || '')}</TableCell>
                        <TableCell className="text-foreground text-sm">{String(row['3pl_stock_qty'] ?? '')}</TableCell>
                        <TableCell className="text-foreground text-sm">{String(row['3pl_can_order_qty'] ?? '')}</TableCell>
                        <TableCell className="text-foreground text-sm">{String(row['3pl_outgoing_qty'] ?? '')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-4 flex gap-4">
                <Button onClick={handleImport} disabled={errors.length > 0 || isProcessing} className="bg-green-600 hover:bg-green-700 text-white">
                  {language === 'zh' ? '確認匯入' : 'Confirm Import'}
                </Button>
                <Button onClick={resetImport} variant="outline" className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50">
                  {language === 'zh' ? '取消' : 'Cancel'}
                </Button>
              </div>
              {/* Errors list */}
              {errors.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 text-red-400 mb-2"><AlertCircle className="w-4 h-4" /> {language === 'zh' ? '驗證錯誤' : 'Validation Errors'} ({errors.length})</div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {errors.map((err, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-red-900/20 border border-red-600/30 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                        <div className="text-sm text-foreground">{language === 'zh' ? '第' : 'Row'} {err.row} {language === 'zh' ? '行' : ''} - {err.field}: {err.message}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Product3PLImport;


